import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Archive, Plus, RotateCcw, Tag as TagIcon } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import {
  TAG_TONE_VALUES,
  type TagScopeType,
  type TagSummaryType,
  type TagToneType,
} from '@aeris/contracts'
import {
  Badge,
  Button,
  EmptyState,
  Input,
  Select,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@aeris/ui'

import { TabHeader } from '../components/config-tabs.tsx'
import { ApiError, createTag, fetchTags, setTagStatus } from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'

/**
 * O vocabulário de marcadores da loja — F088.
 *
 * Duas famílias na mesma tela, em abas, porque são a mesma decisão tomada
 * sobre coisas diferentes: quais rótulos esta loja usa. Separadas porque
 * respondem perguntas diferentes — a do CLIENTE é permanente e segue a pessoa;
 * a da CONVERSA nasce e morre com o assunto —, e oferecer "Aguardando
 * pagamento" para colar num cadastro é como ela ficaria esquecida lá para
 * sempre.
 *
 * A lista inteira de uma vez, sem busca e sem paginação: uma loja tem de cinco
 * a vinte marcadores. A seção 27 da Master Specification fala de listas que
 * crescem para cem mil linhas, e esta não é uma delas.
 *
 * `usageCount` na tabela existe para que arquivar não seja uma decisão às
 * cegas: "Atacado" colada em duzentos clientes é uma coisa, colada em nenhum é
 * outra.
 */

const TONE_LABELS: Record<TagToneType, string> = {
  neutral: 'Cinza',
  info: 'Azul',
  success: 'Verde',
  warning: 'Âmbar',
  danger: 'Vermelho',
}

const SCOPE_COPY: Record<TagScopeType, { title: string; description: string }> =
  {
    party: {
      title: 'Marcadores de cliente',
      description:
        'Ficam no cadastro e acompanham a pessoa em qualquer conversa — "Atacado", "Não fiado", "Cliente do José".',
    },
    conversation: {
      title: 'Marcadores de conversa',
      description:
        'Nascem e morrem com o assunto, e funcionam até para número sem cadastro — "Aguardando pagamento", "Orçamento enviado".',
    },
  }

const TagScopePanel = ({ scope }: { scope: TagScopeType }): ReactNode => {
  const queryClient = useQueryClient()
  const { allows } = useAuth()
  const canManage = allows('tag.manage')

  const [name, setName] = useState('')
  const [tone, setTone] = useState<TagToneType>('neutral')
  const [error, setError] = useState<string | null>(null)

  const query = useQuery({
    queryKey: ['tags', scope, 'all'],
    queryFn: async ({ signal }) => await fetchTags(scope, undefined, signal),
  })

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['tags'] })
  }

  const create = useMutation({
    mutationFn: async () => await createTag({ scope, name, tone }),
    onSuccess: async () => {
      setName('')
      setTone('neutral')
      setError(null)
      await invalidate()
    },
    onError: (cause) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível criar o marcador.'
      )
    },
  })

  const changeStatus = useMutation({
    mutationFn: async (tag: TagSummaryType) =>
      await setTagStatus(tag.id, {
        status: tag.status === 'active' ? 'archived' : 'active',
        version: tag.version,
      }),
    onSuccess: async () => {
      setError(null)
      await invalidate()
    },
    onError: (cause) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível alterar o marcador.'
      )
    },
  })

  const tags = query.data ?? []
  const copy = SCOPE_COPY[scope]

  return (
    <div className="flex flex-col gap-4">
      <p className="text-(length:--text-sm) text-(--text-muted)">
        {copy.description}
      </p>

      {canManage ? (
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault()
            if (name.trim() === '') return
            create.mutate()
          }}
        >
          <label className="flex min-w-48 flex-1 flex-col gap-1">
            <span className="text-(length:--text-xs) text-(--text-muted)">
              Nome
            </span>
            <Input
              value={name}
              maxLength={32}
              placeholder={
                scope === 'party' ? 'Atacado' : 'Aguardando pagamento'
              }
              onChange={(event) => {
                setName(event.target.value)
              }}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-(length:--text-xs) text-(--text-muted)">
              Cor
            </span>
            <Select
              value={tone}
              onChange={(event) => {
                setTone(event.target.value as TagToneType)
              }}
            >
              {TAG_TONE_VALUES.map((value) => (
                <option key={value} value={value}>
                  {TONE_LABELS[value]}
                </option>
              ))}
            </Select>
          </label>
          <Button
            type="submit"
            variant="primary"
            disabled={name.trim() === '' || create.isPending}
            leadingIcon={<Plus aria-hidden="true" className="h-4 w-4" />}
          >
            Criar
          </Button>
        </form>
      ) : null}

      {error === null ? null : (
        <p role="alert" className="text-(length:--text-sm) text-(--danger)">
          {error}
        </p>
      )}

      {query.isPending ? <Spinner label="Carregando marcadores" /> : null}

      {/*
       * O ramo de erro, e não só vazio (F069).
       *
       * Sem ele, uma consulta que falha devolve `data` indefinido, o `?? []`
       * acima vira lista vazia, e a tela AFIRMA que a loja não tem marcador
       * nenhum enquanto o servidor está fora do ar. Quem lê "nenhum ainda"
       * cria tudo de novo; quem lê "não deu para carregar" tenta outra vez.
       */}
      {query.isError ? (
        <EmptyState
          icon={<TagIcon aria-hidden="true" className="h-6 w-6" />}
          title="Não foi possível carregar os marcadores"
          description="Verifique a conexão e tente de novo."
          action={
            <Button
              variant="secondary"
              onClick={() => {
                void query.refetch()
              }}
            >
              Tentar de novo
            </Button>
          }
        />
      ) : null}

      {!query.isPending && !query.isError && tags.length === 0 ? (
        <EmptyState
          icon={<TagIcon aria-hidden="true" className="h-6 w-6" />}
          title="Nenhum marcador ainda"
          description={
            canManage
              ? 'Crie o primeiro acima. Eles aparecem no seletor do cadastro e do atendimento.'
              : 'Quem administra a loja ainda não criou nenhum.'
          }
        />
      ) : null}

      {tags.length === 0 || query.isError ? null : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Marcador</TableHeaderCell>
                <TableHeaderCell>Em uso</TableHeaderCell>
                <TableHeaderCell>Situação</TableHeaderCell>
                {canManage ? (
                  <TableHeaderCell className="text-right">
                    Ações
                  </TableHeaderCell>
                ) : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {tags.map((tag) => (
                <TableRow key={tag.id}>
                  <TableCell>
                    <Badge tone={tag.tone}>{tag.name}</Badge>
                  </TableCell>
                  <TableCell>
                    {tag.usageCount === 0
                      ? '—'
                      : `${String(tag.usageCount)} ${scope === 'party' ? 'cadastro' : 'conversa'}${tag.usageCount === 1 ? '' : 's'}`}
                  </TableCell>
                  <TableCell>
                    {tag.status === 'active' ? (
                      'Ativa'
                    ) : (
                      <span className="text-(--text-muted)">Arquivada</span>
                    )}
                  </TableCell>
                  {canManage ? (
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={changeStatus.isPending}
                        onClick={() => {
                          changeStatus.mutate(tag)
                        }}
                        leadingIcon={
                          tag.status === 'active' ? (
                            <Archive aria-hidden="true" className="h-4 w-4" />
                          ) : (
                            <RotateCcw aria-hidden="true" className="h-4 w-4" />
                          )
                        }
                      >
                        {tag.status === 'active' ? 'Arquivar' : 'Reativar'}
                      </Button>
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  )
}

export const MarcadoresPage = (): ReactNode => (
  <>
    <TabHeader
      title="Marcadores"
      description={
        <>
          Os rótulos que a loja cola em clientes e em conversas. Arquivar tira o
          marcador das telas <strong>sem apagar</strong> onde ele estava colado
          — reativar devolve tudo.
        </>
      }
    />

    <Tabs defaultValue="party">
      <TabsList>
        <TabsTrigger value="party">{SCOPE_COPY.party.title}</TabsTrigger>
        <TabsTrigger value="conversation">
          {SCOPE_COPY.conversation.title}
        </TabsTrigger>
      </TabsList>
      <TabsContent value="party">
        <TagScopePanel scope="party" />
      </TabsContent>
      <TabsContent value="conversation">
        <TagScopePanel scope="conversation" />
      </TabsContent>
    </Tabs>
  </>
)
